import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { useBreadcrumb } from "shared/context/BreadcrumbContext";
import AnalyticsTopBar from "features/analytics/studio/components/shell/AnalyticsTopBar";
import ReportLayout from "features/analytics/studio/components/report/ReportLayout";
import InsightsEmptyState from "features/analytics/studio/components/shell/InsightsEmptyState";
import ChartTypeDropdown from "features/analytics/studio/components/report/ChartTypeDropdown";
import FlowVisualization from "features/analytics/studio/components/charts/FlowVisualization";
import FlowTopPathsView from "features/analytics/studio/components/charts/FlowTopPathsView";
import FlowAutoEventSettings, { ALL_AUTO_EVENTS } from "features/analytics/studio/components/charts/FlowAutoEventSettings";
import QueryBuilderPanel from "features/analytics/studio/components/query/QueryBuilderPanel";
import EventsTimeFilter from "features/activity/components/EventsTimeFilter";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";
import { useAnalytics } from "features/analytics/studio/context/AnalyticsContext";
import { useFlow } from "features/analytics/studio/hooks/useFlow";
import { useEventsMetadata, useCards, useHomeWorkspaceCanvas } from "features/analytics/studio/hooks/useInsights";
import { trackClientEvent } from "shared/utils/trodoEvents";
import { useBoards } from "features/analytics/studio/hooks/useBoards";
import SaveToBoardModal from "features/analytics/studio/components/boards/SaveToBoardModal";
import ChartNameModal from "features/analytics/studio/components/boards/ChartNameModal";
import { isBlankOrDefaultChartTitle } from "features/analytics/studio/utils/chartTitle";
import { useAppDialog } from "shared/context/AppDialogContext";
import * as analyticsApi from "features/analytics/studio/api/analyticsApi";
import { upsertCardInHomeWorkspaceCache } from "shared/utils/homeWorkspaceCache";

const EVENTS_PRESET_TO_ANALYTICS = {
  today: "Today",
  yesterday: "Yesterday",
  "7d": "7D",
  "30d": "30D",
  "3m": "3M",
  "6m": "6M",
  "12m": "12M",
};

function analyticsPresetToEventsPreset(p) {
  if (!p) return "30d";
  const map = { Today: "today", Yesterday: "yesterday", "7D": "7d", "30D": "30d", "3M": "3m", "6M": "6m", "12M": "12m" };
  return map[p] || "30d";
}

function buildFlowDSL(
  anchorEvents,
  conversionWindow,
  conversionCriteria,
  globalFilters,
  legacyBreakdown,
  timeRange,
  flowBreakdownSpec,
  options = {}
) {
  const { expandStepEvents = false, excludedAutoEventNames = [] } = options;
  const fb = flowBreakdownSpec || { mode: "none", cohortIds: [] };
  const mode = fb.mode || "none";
  const flowBreakdown =
    mode === "none"
      ? { mode: "none", cohortIds: [] }
      : mode === "conversion"
        ? { mode: "conversion", cohortIds: [] }
        : { mode: "cohorts", cohortIds: Array.isArray(fb.cohortIds) ? fb.cohortIds : [] };

  const filteredAnchors = anchorEvents.filter((a) => (a.eventName || a.entityName || '').trim());

  return {
    version: 1,
    queryType: "flows",
    time: timeRange,
    definition: {
      anchorEvents: filteredAnchors.map((a) => {
          const name = (a.eventName || a.entityName || '').trim();
          return {
            id: a.id,
            eventName: name,
            entityName: name,
            entityKind: a.entityKind || 'event',
            ...(a.spanKind ? { spanKind: a.spanKind } : {}),
            stepsBefore: a.stepsBefore !== undefined && a.stepsBefore !== null ? a.stepsBefore : 0,
            stepsAfter: a.stepsAfter !== undefined && a.stepsAfter !== null ? a.stepsAfter : 3,
            countSubject: a.countSubject === "group" ? "group" : "user",
            accumulationGroupKey:
              a.countSubject === "group" && a.accumulationGroupKey
                ? String(a.accumulationGroupKey).trim()
                : null,
            filters: (a.filters || []).filter((f) => f.category !== "first_time"),
          };
        }),
      conversionWindow,
      conversionCriteria,
      topN: 3,
      filters: (globalFilters || []).map((f) => ({
        property: f.field ?? f.property,
        operator: f.operator,
        value: f.value,
        value2: f.value2,
        propertyType: f.propertyType,
        propertySource: f.propertySource,
        category: f.category,
      })),
      breakdown: mode === "none" ? legacyBreakdown || null : null,
      flowBreakdown,
      ...(expandStepEvents ? { expandStepEvents: true } : {}),
      ...(excludedAutoEventNames.length > 0 ? { excludedAutoEventNames } : {}),
    },
    visualization: { chartType: "flow-sankey", config: {} },
  };
}

export default function FlowEditorPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { team, boardId } = useParams();
  const isHomeFlow =
    typeof location.pathname === "string" &&
    (location.pathname.includes("/home/flow") || location.pathname.includes("/analytics/home/flow"));
  const [searchParams] = useSearchParams();
  const cardIdParam = searchParams.get("cardId");
  const gridX = searchParams.get("x") != null ? parseInt(searchParams.get("x"), 10) : null;
  const gridY = searchParams.get("y") != null ? parseInt(searchParams.get("y"), 10) : null;
  const gridW = searchParams.get("w") != null ? parseInt(searchParams.get("w"), 10) : null;
  const gridH_param = searchParams.get("h") != null ? parseInt(searchParams.get("h"), 10) : null;

  const selectedTeamData = JSON.parse(localStorage.getItem("selectedTeamData") || "null");
  const teamId = selectedTeamData?._id ?? selectedTeamData?.id ?? null;
  const teamIdOrName = teamId || team;

  const { globalTimeRange, setGlobalTimeRange } = useAnalytics();
  const { result, loading, error, execute, clearResult } = useFlow();
  const { events, loadEvents } = useEventsMetadata();
  const { createBoard, loadBoards } = useBoards();
  const { alert: appAlert } = useAppDialog();
  const effectiveBoardId = boardId && boardId !== "new" ? boardId : null;
  const homeCanvas = useHomeWorkspaceCanvas(teamId, { mode: "default" }, isHomeFlow);
  const boardCards = useCards(isHomeFlow ? null : effectiveBoardId, teamId);
  const cards = isHomeFlow ? homeCanvas.cards : boardCards.cards;
  const loadCards = isHomeFlow ? homeCanvas.loadCards : boardCards.loadCards;
  const updateCard = isHomeFlow ? homeCanvas.updateCard : boardCards.updateCard;
  const editingCard = cardIdParam && cards.find((c) => String(c.id) === String(cardIdParam));

  const [saveBoardModalOpen, setSaveBoardModalOpen] = useState(false);
  const [chartNameModalOpen, setChartNameModalOpen] = useState(false);
  const [saveActionLoading, setSaveActionLoading] = useState(false);
  const [flowSaveLoading, setFlowSaveLoading] = useState(false);
  const pendingSaveRef = useRef(null);
  const saveAnchorRef = useRef(null);
  const [boardName, setBoardName] = useState("");
  useBreadcrumb(
    isHomeFlow
      ? [{ label: "Home", to: `/${team}/home` }]
      : [
          { label: "Reports", to: `/${team}/analytics/reports` },
          boardId && boardId !== "new"
            ? { label: boardName || "Board", to: `/${team}/analytics/boards/${boardId}` }
            : { label: boardName || "Board" },
        ]
  );
  const [cardTitle, setCardTitle] = useState("");
  const [insightTimeFilter, setInsightTimeFilter] = useState(null);
  const [activeTab, setActiveTab] = useState("query");
  useEffect(() => {
    if (activeTab === "annotations") setActiveTab("query");
  }, [activeTab]);
  const [globalFilters, setGlobalFilters] = useState([]);
  const [breakdown, setBreakdown] = useState(null);
  /** Flow-only: conversion split vs cohort compare (not property breakdown). */
  const [flowBreakdown, setFlowBreakdown] = useState({ mode: "none", cohortIds: [] });
  const [activeBreakdown, setActiveBreakdown] = useState("__all__");
  const [anchorEvents, setAnchorEvents] = useState([
    {
      id: "anchor_1",
      eventName: "",
      label: "",
      stepsBefore: 0,
      stepsAfter: 3,
      countSubject: "user",
      accumulationGroupKey: null,
      filters: [],
    },
  ]);
  const [conversionWindow, setConversionWindow] = useState({ value: 7, unit: "days" });
  const [conversionCriteria, setConversionCriteria] = useState("distinct_id");
  /** flows = Sankey; topPaths = ranked path funnels from the same flow graph */
  const [flowChartMode, setFlowChartMode] = useState("flows");
  /** Auto-event names to exclude from intermediate flow steps (empty = show all) */
  const [excludedAutoEventNames, setExcludedAutoEventNames] = useState([]);

  const hydratedRef = useRef(null);
  const executeTimeoutRef = useRef(null);
  /** Query inputs excluding flowBreakdown — when only that changes, skip debounce for faster chart updates */
  const flowQueryRestSigRef = useRef(null);

  // Load board name
  useEffect(() => {
    if (isHomeFlow) {
      setBoardName("My workspace");
      return;
    }
    if (!boardId || boardId === "new") { setBoardName(""); return; }
    let cancelled = false;
    analyticsApi.fetchBoard(boardId, false, teamId).then((res) => {
      if (!cancelled) setBoardName((res?.data ?? res)?.name || "Board");
    }).catch(() => { if (!cancelled) setBoardName("Board"); });
    return () => { cancelled = true; };
  }, [boardId, teamId, isHomeFlow]);

  // Load events metadata
  useEffect(() => {
    if (loadEvents && teamIdOrName) {
      loadEvents(teamIdOrName).catch((err) => console.error("FlowEditorPage: loadEvents error", err));
    }
  }, [loadEvents, teamIdOrName]);

  // Hydrate from existing card
  useEffect(() => {
    if (!cardIdParam || !editingCard) return;
    if (hydratedRef.current === cardIdParam) return;
    hydratedRef.current = cardIdParam;

    const q = editingCard.queryDsl && typeof editingCard.queryDsl === "object" ? editingCard.queryDsl : {};
    setCardTitle(editingCard.title ?? "");

    if (q.definition?.anchorEvents) {
      setAnchorEvents(q.definition.anchorEvents.map((a, i) => ({
        id: a.id || `anchor_${i + 1}`,
        eventName: a.eventName || "",
        label: a.eventName || "",
        stepsBefore: a.stepsBefore !== undefined && a.stepsBefore !== null ? a.stepsBefore : 0,
        stepsAfter: a.stepsAfter !== undefined && a.stepsAfter !== null ? a.stepsAfter : 3,
        countSubject: a.countSubject === "group" ? "group" : "user",
        accumulationGroupKey: a.accumulationGroupKey || null,
        filters: a.filters || [],
      })));
    }
    if (q.definition?.conversionWindow) setConversionWindow(q.definition.conversionWindow);
    if (q.definition?.conversionCriteria) setConversionCriteria(q.definition.conversionCriteria);
    if (q.definition?.filters) {
      setGlobalFilters(q.definition.filters.map((f, i) => ({
        ...f, id: f.id || `gf-${i}`, connector: i === 0 ? "WHERE" : "AND",
      })));
    }
    if (q.definition?.flowBreakdown) {
      setFlowBreakdown({
        mode: q.definition.flowBreakdown.mode || "none",
        cohortIds: Array.isArray(q.definition.flowBreakdown.cohortIds)
          ? q.definition.flowBreakdown.cohortIds.map((id) => Number(id))
          : [],
      });
    } else {
      setFlowBreakdown({ mode: "none", cohortIds: [] });
    }
    if (q.definition?.breakdown) setBreakdown(q.definition.breakdown);
    setExcludedAutoEventNames(
      Array.isArray(q.definition?.excludedAutoEventNames) ? q.definition.excludedAutoEventNames : []
    );

    const t = q.time;
    if (t?.start && t?.end) {
      setInsightTimeFilter({ type: "custom", from: t.start, to: t.end });
    } else if (t?.preset) {
      setInsightTimeFilter(null);
      setGlobalTimeRange(t.preset);
    }
  }, [cardIdParam, editingCard, setGlobalTimeRange]);

  // Default segment for property breakdown ("All" vs slice). Cohort flow uses merged chart — no tab.
  useEffect(() => {
    setActiveBreakdown("__all__");
  }, [result]);

  // Auto-execute (debounced; no debounce when only flow breakdown mode changes)
  useEffect(() => {
    const validAnchors = anchorEvents.filter((a) => a.eventName && a.eventName.trim());
    if (validAnchors.length === 0) {
      flowQueryRestSigRef.current = null;
      clearResult();
      return;
    }

    const timeRange = getTimeRange();
    const effectiveFlowBreakdown =
      flowBreakdown.mode === "cohorts" &&
      (!Array.isArray(flowBreakdown.cohortIds) || flowBreakdown.cohortIds.length === 0)
        ? { mode: "none", cohortIds: [] }
        : flowBreakdown;

    const restSig = JSON.stringify({
      a: validAnchors.map((x) => ({
        eventName: x.eventName,
        stepsBefore: x.stepsBefore,
        stepsAfter: x.stepsAfter,
        countSubject: x.countSubject,
        accumulationGroupKey: x.accumulationGroupKey,
        filters: x.filters,
      })),
      conversionWindow,
      conversionCriteria,
      globalFilters,
      breakdown,
      timeRange,
      teamIdOrName,
      excludedAutoEventNames,
    });
    const debounceMs =
      flowQueryRestSigRef.current != null && flowQueryRestSigRef.current === restSig ? 0 : 400;
    flowQueryRestSigRef.current = restSig;

    const dsl = buildFlowDSL(
      validAnchors,
      conversionWindow,
      conversionCriteria,
      globalFilters,
      breakdown,
      timeRange,
      effectiveFlowBreakdown,
      { expandStepEvents: flowChartMode === "topPaths", excludedAutoEventNames }
    );

    if (executeTimeoutRef.current) clearTimeout(executeTimeoutRef.current);
    executeTimeoutRef.current = setTimeout(() => {
      executeTimeoutRef.current = null;
      execute(dsl, teamIdOrName);
    }, debounceMs);

    return () => {
      if (executeTimeoutRef.current) { clearTimeout(executeTimeoutRef.current); executeTimeoutRef.current = null; }
    };
  }, [anchorEvents, conversionWindow, conversionCriteria, globalFilters, breakdown, flowBreakdown, flowChartMode, excludedAutoEventNames, globalTimeRange, insightTimeFilter, teamIdOrName]); // eslint-disable-line

  function getTimeRange() {
    if (insightTimeFilter?.type === "preset" && insightTimeFilter.preset) {
      return { preset: EVENTS_PRESET_TO_ANALYTICS[insightTimeFilter.preset] || globalTimeRange || "30D", start: null, end: null, interval: "day", timezone: "UTC" };
    }
    if (insightTimeFilter?.from && insightTimeFilter?.to) {
      return { preset: null, start: insightTimeFilter.from, end: insightTimeFilter.to, interval: "day", timezone: "UTC" };
    }
    return { preset: globalTimeRange || "30D", start: null, end: null, interval: "day", timezone: "UTC" };
  }

  const executeFlowSave = async (effectiveTitle) => {
    const validAnchors = anchorEvents.filter((a) => a.eventName && a.eventName.trim());
    if (validAnchors.length === 0) {
      await appAlert("Please select at least one event.", { title: "Cannot save" });
      return;
    }

    try {
      const dsl = buildFlowDSL(
        validAnchors,
        conversionWindow,
        conversionCriteria,
        globalFilters,
        breakdown,
        getTimeRange(),
        flowBreakdown,
        { expandStepEvents: flowChartMode === "topPaths", excludedAutoEventNames }
      );

      if (cardIdParam && editingCard && (isHomeFlow || boardId)) {
        const updated = await updateCard(cardIdParam, {
          title: effectiveTitle,
          queryDsl: dsl,
          visualization: { chartType: "flow-sankey", config: {} },
        });
        if (isHomeFlow && teamId != null && updated) upsertCardInHomeWorkspaceCache(teamId, updated);
        navigate(isHomeFlow ? `/${team}/home` : `/${team}/analytics/boards/${boardId}`);
        return;
      }

      const position =
        gridX != null && gridY != null
          ? { x: gridX, y: gridY, w: gridW ?? 8, h: gridH_param ?? 5 }
          : undefined;
      const cardBody = {
        cardType: "flow",
        title: effectiveTitle,
        queryDsl: dsl,
        visualization: { chartType: "flow-sankey", config: {} },
        ...(position && { position }),
      };

      if (isHomeFlow) {
        const res = await analyticsApi.createHomeWorkspaceCard(cardBody, teamId);
        const newCard = res?.data;
        if (newCard && teamId != null) upsertCardInHomeWorkspaceCache(teamId, newCard);
        navigate(`/${team}/home`);
        return;
      }

      if (boardId && boardId !== "new") {
        await analyticsApi.createCard(boardId, cardBody, teamId);
        navigate(`/${team}/analytics/boards/${boardId}`);
        return;
      }

      pendingSaveRef.current = { cardBody };
      setSaveBoardModalOpen(true);
    } catch (err) {
      console.error("FlowEditorPage: save failed", err);
      await appAlert("Failed to save. Please try again.", { title: "Save failed", variant: "error" });
    }
  };

  const handleSave = async () => {
    const validAnchors = anchorEvents.filter((a) => a.eventName && a.eventName.trim());
    if (validAnchors.length === 0) {
      await appAlert("Please select at least one event.", { title: "Cannot save" });
      return;
    }

    const t = (cardTitle || "").trim();
    if (isBlankOrDefaultChartTitle(t)) {
      setChartNameModalOpen(true);
      return;
    }

    setFlowSaveLoading(true);
    trackClientEvent(
      'flow_saved',
      { anchor_event_count: validAnchors.length },
      { category: 'product' }
    );
    try {
      await executeFlowSave(t);
    } finally {
      setFlowSaveLoading(false);
    }
  };

  const handleChartNameModalConfirm = (name) => {
    setCardTitle(name);
    setChartNameModalOpen(false);
    setFlowSaveLoading(true);
    void executeFlowSave(name.trim()).finally(() => setFlowSaveLoading(false));
  };

  const finishSaveToExistingBoard = async (targetBoardId) => {
    const p = pendingSaveRef.current;
    if (!p?.cardBody) return;
    setSaveActionLoading(true);
    try {
      await analyticsApi.createCard(targetBoardId, p.cardBody, teamId);
      pendingSaveRef.current = null;
      setSaveBoardModalOpen(false);
      navigate(`/${team}/analytics/boards/${targetBoardId}`);
    } catch (err) {
      console.error("FlowEditorPage: save failed", err);
      await appAlert("Failed to save. Please try again.", { title: "Save failed", variant: "error" });
    } finally {
      setSaveActionLoading(false);
    }
  };

  const finishSaveToNewBoard = async () => {
    const p = pendingSaveRef.current;
    if (!p?.cardBody) return;
    setSaveActionLoading(true);
    try {
      const name = (p.cardBody.title || "").trim() || "New board";
      const newBoard = await createBoard({
        name,
        description: "",
        time_range: { preset: globalTimeRange, interval: "day", timezone: "UTC" },
        filters: null,
        compare_mode: null,
        layout: null,
      }, teamId);
      const newId = String(newBoard.id);
      await analyticsApi.createCard(newId, p.cardBody, teamId);
      pendingSaveRef.current = null;
      setSaveBoardModalOpen(false);
      navigate(`/${team}/analytics/boards/${newId}`);
    } catch (err) {
      console.error("FlowEditorPage: save failed", err);
      await appAlert("Failed to save. Please try again.", { title: "Save failed", variant: "error" });
    } finally {
      setSaveActionLoading(false);
    }
  };

  const handleFlowBreakdownChange = (fb) => {
    setFlowBreakdown(fb);
    if (fb.mode !== "none") setBreakdown(null);
  };

  const handleClose = () => {
    if (isHomeFlow) navigate(`/${team}/home`);
    else navigate(`/${team}/analytics/boards/${boardId || "new"}`);
  };

  const hasAnchor = anchorEvents.some((a) => a.eventName && a.eventName.trim());
  const validAnchorCount = anchorEvents.filter((a) => a.eventName && a.eventName.trim()).length;

  // Auto-reset chart mode when dropping below 2 events (Top Paths requires ≥2 anchors)
  useEffect(() => {
    if (validAnchorCount < 2 && flowChartMode === "topPaths") {
      setFlowChartMode("flows");
    }
  }, [validAnchorCount, flowChartMode]);

  const flowHeaderContent = (
    <div className="flex items-center gap-3 flex-wrap">
      <EventsTimeFilter
        selectedPreset={
          insightTimeFilter
            ? insightTimeFilter.type === "custom"
              ? "custom"
              : insightTimeFilter.preset || "30d"
            : analyticsPresetToEventsPreset(globalTimeRange)
        }
        onTimeFilterChange={(filter) => {
          setInsightTimeFilter(filter);
          if (filter.type === "preset" && filter.preset) {
            setGlobalTimeRange(EVENTS_PRESET_TO_ANALYTICS[filter.preset] || globalTimeRange);
          }
        }}
      />
      <ChartTypeDropdown
        reportType="flow"
        chartType={flowChartMode}
        onChartTypeChange={setFlowChartMode}
        theme={theme}
        disabledOptionIds={validAnchorCount < 2 ? ["topPaths"] : []}
      />
      <FlowAutoEventSettings
        excludedAutoEventNames={excludedAutoEventNames}
        onExcludedChange={setExcludedAutoEventNames}
        theme={theme}
      />
    </div>
  );

  return (
    <>
    <ReportLayout
      topBar={
        <AnalyticsTopBar
          chartName={cardTitle}
          onChartNameChange={(e) => setCardTitle(e.target.value)}
          showCreateNew={false}
          showTimeRange={false}
          showSaveClose={true}
          onSave={handleSave}
          onClose={handleClose}
          saveAnchorRef={saveAnchorRef}
          saveStatus={flowSaveLoading ? "saving" : undefined}
        />
      }
      reportType="flow"
      headerContent={flowHeaderContent}
      headerProps={{}}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      theme={theme}
      rightPanel={
        <QueryBuilderPanel
          activeTab={activeTab}
          onTabChange={setActiveTab}
          reportType="flow"
          flowAnchorEvents={anchorEvents}
          onFlowAnchorEventsChange={setAnchorEvents}
          flowConversionWindow={conversionWindow}
          onFlowConversionWindowChange={setConversionWindow}
          flowConversionCriteria={conversionCriteria}
          onFlowConversionCriteriaChange={setConversionCriteria}
          availableEvents={events}
          teamId={teamIdOrName}
          globalFilters={globalFilters}
          onGlobalFiltersChange={setGlobalFilters}
          breakdowns={breakdown ? [breakdown] : []}
          onBreakdownsChange={(arr) => setBreakdown(arr[0] || null)}
          flowBreakdown={flowBreakdown}
          onFlowBreakdownChange={handleFlowBreakdownChange}
        />
      }
    >
      {!hasAnchor ? (
        <InsightsEmptyState />
      ) : loading ? (
        <div className="p-6 flex-1 flex items-center justify-center min-h-[400px]">
          <div className="text-sm" style={{ color: theme.textMuted }}>
            Loading flow data…
          </div>
        </div>
      ) : error ? (
        <div className="p-6 flex-1 flex items-center justify-center min-h-[400px]">
          <div className="text-sm" style={{ color: "var(--feedback-error)" }}>
            Error: {error}
          </div>
        </div>
      ) : result ? (
        <div className="min-w-0 flex-1 flex flex-col p-6">
            {flowChartMode === "topPaths" ? (
              <>
                {result.breakdown?.values?.length > 0 &&
                  result.breakdown.mode !== "conversion" &&
                  result.breakdown.mode !== "cohorts" && (
                  <div
                    className="flex gap-2 px-4 py-2.5 mb-3 flex-wrap border-b rounded-lg"
                    style={{
                      borderColor: theme.border,
                      backgroundColor: `${theme.primaryLight}14`,
                    }}
                  >
                    {["__all__", ...result.breakdown.values].map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setActiveBreakdown(v)}
                        className="px-3 py-1 rounded-full text-xs transition-colors"
                        style={{
                          background: activeBreakdown === v ? (theme.accent || "#7c3aed") : (theme.accentSoft || "#f3f0ff"),
                          color: activeBreakdown === v ? "var(--surface-base)" : (theme.textPrimary || "var(--text-primary)"),
                        }}
                      >
                        {v === "__all__" ? "All" : v}
                      </button>
                    ))}
                  </div>
                )}
                <FlowTopPathsView
                  flowResult={(() => {
                    const br = result.breakdown;
                    if (!br) return result;
                    if (br.mode === "conversion" || br.mode === "cohorts") return result;
                    if (activeBreakdown !== "__all__" && br.data?.[activeBreakdown]) {
                      return { ...result, ...br.data[activeBreakdown] };
                    }
                    return result;
                  })()}
                  height={520}
                />
              </>
            ) : (
              <div
                className="rounded-xl border overflow-hidden"
                style={{ borderColor: theme.border }}
              >
                {result.breakdown?.values?.length > 0 &&
                  result.breakdown.mode !== "conversion" &&
                  result.breakdown.mode !== "cohorts" && (
                  <div
                    className="flex gap-2 px-4 py-2.5 flex-wrap border-b rounded-t-xl"
                    style={{
                      borderColor: theme.border,
                      backgroundColor: `${theme.primaryLight}14`,
                    }}
                  >
                    {["__all__", ...result.breakdown.values].map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setActiveBreakdown(v)}
                        className="px-3 py-1 rounded-full text-xs transition-colors"
                        style={{
                          background: activeBreakdown === v ? (theme.accent || "#7c3aed") : (theme.accentSoft || "#f3f0ff"),
                          color: activeBreakdown === v ? "var(--surface-base)" : (theme.textPrimary || "var(--text-primary)"),
                        }}
                      >
                        {v === "__all__" ? "All" : v}
                      </button>
                    ))}
                  </div>
                )}
                <FlowVisualization
                  data={(() => {
                    const br = result.breakdown;
                    if (!br) return result;
                    if (br.mode === "conversion" || br.mode === "cohorts") return result;
                    if (activeBreakdown !== "__all__" && br.data?.[activeBreakdown]) {
                      return { ...result, ...br.data[activeBreakdown] };
                    }
                    return result;
                  })()}
                  height={520}
                  flowAnchorEvents={anchorEvents}
                />
              </div>
            )}
        </div>
      ) : null}
    </ReportLayout>
    <ChartNameModal
      open={chartNameModalOpen}
      onClose={() => setChartNameModalOpen(false)}
      initialValue={cardTitle}
      onConfirm={handleChartNameModalConfirm}
    />
    <SaveToBoardModal
      open={saveBoardModalOpen}
      anchorRef={saveAnchorRef}
      onClose={() => {
        if (!saveActionLoading) {
          setSaveBoardModalOpen(false);
          pendingSaveRef.current = null;
        }
      }}
      teamId={teamId}
      loadBoards={loadBoards}
      onSaveToExisting={finishSaveToExistingBoard}
      onSaveToNewBoard={finishSaveToNewBoard}
      saving={saveActionLoading}
    />
    </>
  );
}
